// Phase 12 – Notifications: Bell widget, Center, Preferences, Templates,
// Providers, Delivery Logs, Announcements, Dashboard.
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell, Mail, MessageSquare, Phone, Check, Trash2, Send, Sparkles, Loader2,
  Settings2, FileText, ClipboardList, Megaphone, Layers, Cog, TrendingUp,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";

// ============================================================
// NOTIFICATION BELL (topbar widget)
// ============================================================

export function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    const iv = setInterval(load, 30000);
    load();
    return () => clearInterval(iv);
  }, []);
  const load = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.data?.count || 0);
    } catch { /* ignore */ }
  };
  const openPanel = async () => {
    setOpen(true);
    try {
      const { data } = await api.get("/notifications");
      setItems((data.data?.notifications || []).slice(0, 6));
    } catch { /* ignore */ }
  };
  const markRead = async (id) => {
    try { await api.patch(`/notifications/${id}/read`); load(); openPanel(); }
    catch { /* ignore */ }
  };
  return (
    <div className="relative" data-testid="notification-bell">
      <button type="button" onClick={openPanel} className="relative rounded-lg p-2 hover:bg-slate-100" data-testid="notification-bell-btn">
        <Bell className="h-5 w-5 text-slate-700" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white" data-testid="notification-unread-count">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}></div>
          <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-2xl border border-slate-200 bg-white shadow-xl" data-testid="notification-panel">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Notifications</div>
              <Link to="/app/notifications" onClick={() => setOpen(false)} className="text-xs text-[#101876] hover:underline" data-testid="notification-view-all">View all</Link>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-500">No notifications yet.</div>
              ) : items.map((n) => (
                <button key={n.id} type="button" onClick={() => markRead(n.id)} data-testid={`notif-item-${n.id}`}
                        className={`w-full text-left border-b border-slate-100 px-4 py-3 hover:bg-slate-50 ${!n.read_status ? "bg-blue-50/40" : ""}`}>
                  <div className="text-sm font-semibold text-slate-800">{n.notification_title}</div>
                  <div className="mt-0.5 text-[11px] text-slate-600 line-clamp-2">{n.notification_message}</div>
                  <div className="mt-1 text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString()}</div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// NOTIFICATION CENTER
// ============================================================

export function NotificationCenterPage() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const { data } = await api.get("/notifications" + (filter === "unread" ? "?unread_only=true" : ""));
      setRows(data.data?.notifications || []);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, [filter]);
  const markAll = async () => {
    try { const { data } = await api.patch("/notifications/mark-all-read"); toast.success(data.message); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (id) => {
    try { await api.delete(`/notifications/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const markRead = async (id) => {
    try { await api.patch(`/notifications/${id}/read`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Notifications"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Notifications" breadcrumbs={["Notifications"]}>
      <div className="space-y-3" data-testid="notification-center">
        <div className="flex items-center justify-between">
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
            {[["all", "All"], ["unread", "Unread"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`nc-filter-${k}`}
                      className={`rounded-md px-3 py-1 text-xs font-semibold ${filter === k ? "bg-[#101876] text-white" : "text-slate-600"}`}>{l}</button>
            ))}
          </div>
          <button type="button" onClick={markAll} data-testid="nc-mark-all"
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">
            <Check className="inline h-3.5 w-3.5 mr-1" /> Mark all read
          </button>
        </div>
        {rows.length === 0 ? (
          <EmptyState title="No notifications" description="You're all caught up." icon={Bell} />
        ) : rows.map((n) => (
          <div key={n.id} className={`rounded-2xl border p-4 ${!n.read_status ? "border-[#101876]/30 bg-blue-50/30" : "border-slate-200 bg-white"}`} data-testid={`nc-item-${n.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${!n.read_status ? "bg-[#329112]" : "bg-slate-300"}`}></span>
                  <div className="text-sm font-bold text-slate-800">{n.notification_title}</div>
                  {n.priority === "critical" && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">CRITICAL</span>}
                </div>
                <div className="mt-1 text-xs text-slate-600">{n.notification_message}</div>
                <div className="mt-1 text-[10px] text-slate-400">{n.module_name} · {new Date(n.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                {!n.read_status && (
                  <button type="button" onClick={() => markRead(n.id)} data-testid={`nc-read-${n.id}`} className="text-[#329112]"><Check className="h-4 w-4" /></button>
                )}
                <button type="button" onClick={() => del(n.id)} data-testid={`nc-del-${n.id}`} className="text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// USER PREFERENCES
// ============================================================

export function NotificationPreferencesPage() {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/user/notification-preferences"); setPrefs(data.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  const save = async () => {
    try { await api.put("/user/notification-preferences", prefs); toast.success("Preferences saved"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const tog = (k) => setPrefs({ ...prefs, [k]: !prefs[k] });
  if (!prefs) return <DashboardLayout title="Preferences"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Notification Preferences" breadcrumbs={["Settings", "Notifications"]}>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-2xl space-y-3" data-testid="notification-preferences">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Channels</div>
        {[
          ["email_enabled", "Email notifications", Mail],
          ["sms_enabled", "SMS notifications", Phone],
          ["whatsapp_enabled", "WhatsApp notifications", MessageSquare],
          ["in_app_enabled", "In-app notifications", Bell],
        ].map(([k, label, Icon]) => (
          <label key={k} className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50 cursor-pointer" data-testid={`pref-${k}`}>
            <span className="flex items-center gap-2 text-sm"><Icon className="h-4 w-4 text-[#101876]" />{label}</span>
            <input type="checkbox" checked={!!prefs[k]} onChange={() => tog(k)} className="h-4 w-4" />
          </label>
        ))}
        <div className="border-t pt-3 text-xs font-bold uppercase tracking-widest text-slate-500">Notification types</div>
        {[
          ["course_reminders_enabled", "Course reminders"],
          ["exam_reminders_enabled", "Exam reminders"],
          ["certificate_reminders_enabled", "Certificate reminders"],
          ["billing_notifications_enabled", "Billing notifications"],
          ["announcements_enabled", "Announcements"],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50 cursor-pointer" data-testid={`pref-${k}`}>
            <span className="text-sm">{label}</span>
            <input type="checkbox" checked={!!prefs[k]} onChange={() => tog(k)} className="h-4 w-4" />
          </label>
        ))}
        <div className="text-[10px] text-slate-400 mt-2">Critical system notifications (OTP, password reset, security, subscription expiry) cannot be disabled.</div>
        <button type="button" onClick={save} data-testid="pref-save"
                className="mt-3 rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">Save preferences</button>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SUPER ADMIN — DASHBOARD, PROVIDERS, TEMPLATES, LOGS
// ============================================================

export function AdminNotificationDashboardPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data: r } = await api.get("/admin/notification-dashboard"); setData(r.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!data) return <DashboardLayout title="Notifications Dashboard"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Notification Dashboard" breadcrumbs={["Super Admin", "Notifications"]}>
      <div className="space-y-4" data-testid="admin-notif-dashboard">
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total sent" value={data.total_sent} icon="Send" tone="navy" testId="nd-kpi-total" index={0} />
          <StatCard label="Today" value={data.today_sent} icon="Sparkles" tone="green" testId="nd-kpi-today" index={1} />
          <StatCard label="Failed" value={data.failed} icon="AlertTriangle" tone="rose" testId="nd-kpi-failed" index={2} />
          <StatCard label="Success rate" value={`${data.delivery_success_pct}%`} icon="TrendingUp" tone="green" testId="nd-kpi-rate" index={3} />
        </section>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">By channel</div>
          {data.by_channel.length === 0 ? (
            <EmptyState title="No delivery data" description="Notifications will appear here as they're sent." icon={Bell} />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-slate-400"><tr><th className="text-left py-2">Channel</th><th className="text-right">Sent</th><th className="text-right">Failed</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.by_channel.map((c) => (
                  <tr key={c.channel}><td className="py-2 capitalize">{c.channel.replace(/_/g, " ")}</td><td className="text-right">{c.sent}</td><td className="text-right">{c.failed}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export function AdminNotificationProvidersPage() {
  const [rows, setRows] = useState(null);
  const [testResult, setTestResult] = useState(null);
  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await api.get("/admin/notification-providers"); setRows(data.data?.providers || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  const test = async (id) => {
    try { const { data } = await api.post(`/admin/notification-providers/${id}/test`); setTestResult({ id, ...data.data }); toast.success("Test sent"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Providers"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Notification Providers" breadcrumbs={["Super Admin", "Providers"]}>
      <div className="space-y-3" data-testid="admin-notif-providers">
        <div className="text-xs text-slate-500 italic">Secrets are stored via env variables. RESEND_API_KEY absent → CONSOLE mode.</div>
        {rows.map((p) => (
          <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`provider-${p.provider_code}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold">{p.provider_name}
                  {p.is_default && <span className="ml-2 rounded-full bg-[#329112]/10 px-2 py-0.5 text-[10px] font-bold text-[#329112]">DEFAULT</span>}
                </div>
                <div className="text-[11px] text-slate-500 font-mono">{p.provider_code} · {p.channel_type}{p.from_email ? ` · from ${p.from_email}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                {p.mode === "console" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">CONSOLE</span>}
                {p.mode === "live" && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-800">LIVE</span>}
                <button type="button" onClick={() => test(p.id)} data-testid={`provider-test-${p.provider_code}`}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">Test</button>
              </div>
            </div>
            {testResult?.id === p.id && (
              <div className="mt-2 rounded-lg bg-slate-50 p-2 text-xs">
                Status: <strong>{testResult.status}</strong>{testResult.recipient ? ` → ${testResult.recipient}` : ""} · ID: {testResult.message_id}
              </div>
            )}
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

export function AdminNotificationTemplatesPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/notification-templates"); setRows(data.data?.templates || []); }
      catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  if (rows === null) return <DashboardLayout title="Templates"><LoadingBlock /></DashboardLayout>;
  const groups = rows.reduce((acc, t) => { (acc[t.channel] = acc[t.channel] || []).push(t); return acc; }, {});
  return (
    <DashboardLayout title="Notification Templates" breadcrumbs={["Super Admin", "Templates"]}>
      <div className="space-y-4" data-testid="admin-notif-templates">
        {Object.entries(groups).map(([channel, tpls]) => (
          <div key={channel} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">{channel.replace(/_/g, " ")} ({tpls.length})</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {tpls.map((t) => (
                <div key={t.id} className="rounded-lg border border-slate-100 bg-slate-50/50 p-3" data-testid={`tpl-${t.template_code}-${t.channel}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">{t.template_name}</div>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${t.status === "active" ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-500"}`}>{t.status}</span>
                  </div>
                  <div className="mt-1 font-mono text-[10px] text-slate-400">{t.template_code} · {t.event_type}</div>
                  {t.subject && <div className="mt-1 text-xs text-slate-700 truncate">{t.subject}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

export function AdminDeliveryLogsPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/notification-delivery-logs"); setRows(data.data?.logs || []); }
      catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  if (rows === null) return <DashboardLayout title="Delivery Logs"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Delivery Logs" breadcrumbs={["Super Admin", "Delivery Logs"]}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="admin-delivery-logs">
        {rows.length === 0 ? (
          <EmptyState title="No logs yet" description="Delivery events will appear here." icon={ClipboardList} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="p-3 text-left">Time</th><th className="p-3">Channel</th><th className="p-3">Event</th><th className="p-3">Recipient</th><th className="p-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((l) => (
                <tr key={l.id} data-testid={`log-${l.id}`}>
                  <td className="p-3 text-[11px] text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="p-3 capitalize">{l.channel}</td>
                  <td className="p-3 text-xs">{l.event_type}</td>
                  <td className="p-3 text-xs font-mono">{l.recipient}</td>
                  <td className="p-3 text-center">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      l.status === "sent" ? "bg-green-100 text-green-800" :
                      l.status === "sent_console" ? "bg-amber-100 text-amber-800" :
                      "bg-rose-100 text-rose-700"
                    }`}>{l.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// ANNOUNCEMENTS (Super Admin / Hospital Admin / Faculty)
// ============================================================

export function AdminAnnouncementsPage() { return <AnnouncementsGeneric scope="admin" />; }
export function OrgAnnouncementsPage() { return <AnnouncementsGeneric scope="org" />; }
export function FacultyAnnouncementsPage() { return <AnnouncementsGeneric scope="faculty" />; }

function AnnouncementsGeneric({ scope }) {
  const [rows, setRows] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [channels, setChannels] = useState(["in_app", "email"]);
  const [role, setRole] = useState("");
  const basePath = scope === "admin" ? "/admin/announcements" : scope === "org" ? "/org/announcements" : "/faculty/announcements";
  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await api.get(basePath); setRows(data.data?.announcements || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  const create = async () => {
    if (!title.trim() || !message.trim()) return toast.error("Fill title + message");
    try {
      await api.post(basePath, { announcement_title: title, message,
                                  target_audience_json: role ? { role } : {},
                                  channels_json: channels });
      toast.success("Announcement created");
      setTitle(""); setMessage(""); setShowForm(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const send = async (id) => {
    try { const { data } = await api.post(`${basePath}/${id}/send`); toast.success(`Sent to ${data.data?.recipient_count} users`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const preview = async (id) => {
    try { const { data } = await api.post(`${basePath}/${id}/preview-recipients`); toast.info(`Total recipients: ${data.data?.total}`); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Announcements"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Announcements" breadcrumbs={["Announcements"]}>
      <div className="space-y-3" data-testid={`announcements-${scope}`}>
        <button type="button" onClick={() => setShowForm(!showForm)} data-testid="ann-create-toggle"
                className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">
          {showForm ? "Cancel" : "+ New announcement"}
        </button>
        {showForm && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="ann-form">
            <input type="text" placeholder="Announcement title" value={title} onChange={(e) => setTitle(e.target.value)}
                   data-testid="ann-title" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <textarea rows={4} placeholder="Message" value={message} onChange={(e) => setMessage(e.target.value)}
                      data-testid="ann-message" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <div className="flex flex-wrap gap-3 text-xs">
              {["in_app", "email", "sms", "whatsapp"].map((c) => (
                <label key={c} className="flex items-center gap-1"><input type="checkbox" checked={channels.includes(c)}
                       onChange={() => setChannels(channels.includes(c) ? channels.filter(x => x !== c) : [...channels, c])}
                       data-testid={`ann-channel-${c}`} /> {c.replace("_", " ")}</label>
              ))}
            </div>
            <button type="button" onClick={create} data-testid="ann-submit"
                    className="rounded-lg bg-[#329112] px-4 py-2 text-sm font-semibold text-white">Save announcement</button>
          </div>
        )}
        {rows.length === 0 ? (
          <EmptyState title="No announcements" description="Create your first announcement." icon={Megaphone} />
        ) : rows.map((a) => (
          <div key={a.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`ann-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-sm font-bold">{a.announcement_title}</div>
                <div className="mt-1 text-xs text-slate-600">{a.message}</div>
                <div className="mt-1 text-[10px] text-slate-400">Channels: {(a.channels_json || []).join(", ")} · {a.status}</div>
              </div>
              <div className="flex items-center gap-2">
                {a.status !== "sent" && (<>
                  <button type="button" onClick={() => preview(a.id)} data-testid={`ann-preview-${a.id}`} className="text-xs text-[#101876] underline">Preview</button>
                  <button type="button" onClick={() => send(a.id)} data-testid={`ann-send-${a.id}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-[#329112] px-3 py-1.5 text-xs font-semibold text-white">
                    <Send className="h-3.5 w-3.5" /> Send
                  </button>
                </>)}
                {a.status === "sent" && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-800">SENT ({a.recipient_count || 0})</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// ORG NOTIFICATION SETTINGS
// ============================================================

export function OrgNotificationSettingsPage() {
  const [s, setS] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/org/notification-settings"); setS(data.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  const save = async () => {
    try { await api.put("/org/notification-settings", s); toast.success("Saved"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!s) return <DashboardLayout title="Notification Settings"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Organization Notification Settings" breadcrumbs={["Hospital Admin", "Notification Settings"]}>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-2xl space-y-3" data-testid="org-notif-settings">
        {[
          ["email_notifications_enabled", "Enable email notifications"],
          ["sms_notifications_enabled", "Enable SMS notifications"],
          ["whatsapp_notifications_enabled", "Enable WhatsApp notifications"],
          ["department_head_notifications_enabled", "Notify department heads on compliance events"],
        ].map(([k, l]) => (
          <label key={k} className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50 cursor-pointer" data-testid={`os-${k}`}>
            <span className="text-sm">{l}</span>
            <input type="checkbox" checked={!!s[k]} onChange={() => setS({ ...s, [k]: !s[k] })} className="h-4 w-4" />
          </label>
        ))}
        <div className="border-t pt-3">
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Organization email</label>
          <input type="email" value={s.organization_notification_email || ""} onChange={(e) => setS({ ...s, organization_notification_email: e.target.value })}
                 data-testid="os-email" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Compliance alert threshold (%)</label>
          <input type="number" value={s.compliance_alert_threshold || 75} onChange={(e) => setS({ ...s, compliance_alert_threshold: parseInt(e.target.value) || 75 })}
                 data-testid="os-threshold" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </div>
        <button type="button" onClick={save} data-testid="os-save"
                className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">Save settings</button>
      </div>
    </DashboardLayout>
  );
}
